import React, { forwardRef } from "react";
import styled from "styled-components";
import { tokens } from "../../public/tokens";

const sizes = {
    xs: { boxSize: tokens.globals["spacing-6"], fontSize: tokens.typography.fontSize["md-12px"] },
    sm: { boxSize: tokens.globals["spacing-7"], fontSize: tokens.typography.fontSize["md-12px"] },
    md: { boxSize: tokens.globals["spacing-8"], fontSize: tokens.typography.fontSize["lg-14px"] },
    lg: { boxSize: tokens.globals["spacing-9"], fontSize: tokens.typography.fontSize["lg-14px"] },
};

const variants = {
    default: {
        color: tokens.colors.text.secondary,
        hoverBackground: tokens.colors.neutral.grey.Alpha["10"],
        focusOutlineColor: tokens.colors.brand.blue["500"],
    },
    inverse: {
        color: tokens.colors.neutral.white,
        hoverBackground: "rgba(255, 255, 255, 0.15)",
        focusOutlineColor: tokens.colors.neutral.white,
    },
};

const StyledButton = styled.button(({ $size = "lg", $variant = "default" }) => {
    const size = sizes[$size] || sizes.lg;
    const variant = variants[$variant] || variants.default;

    return {
        width: size.boxSize,
        height: size.boxSize,
        flexShrink: 0,
        padding: 0,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        background: "transparent",
        border: "none",
        borderRadius: tokens.globals["border-radius"],
        cursor: "pointer",
        color: variant.color,
        fontSize: size.fontSize,

        "&:hover:not(:disabled)": { background: variant.hoverBackground },
        "&:focus-visible": {
            outline: `${tokens.globals["spacing-px"]} solid ${variant.focusOutlineColor}`,
            outlineOffset: tokens.globals["spacing-0-5"],
        },
        "&:disabled": { opacity: 0.5, cursor: "not-allowed" },
    };
});

/**
 * Transparent, icon-only AIE button with a soft hover highlight — for a lone icon action that
 * shouldn't look like a bordered/filled button (e.g. a dialog's close (x), a panel's overflow
 * trigger). Unlike `IconButton`, it carries no background/border/shadow of its own at rest.
 * @param {React.ReactNode} props.children The icon to render, e.g. `<i className="fal fa-times" />`
 *   — this component has no icon of its own, so the caller always supplies one.
 * @param {"default"|"inverse"} [props.variant="default"] "inverse" is for a colored/dark
 *   background (white icon, translucent white hover) instead of the light-surface default.
 * @param {"xs"|"sm"|"md"|"lg"} [props.size="lg"] Square box size.
 * @param {React.Ref} ref Forwarded to the underlying button element.
 * Also: disabled, onClick, aria-label, title, className (self-explanatory).
 */
const PlainIconButton = forwardRef(function PlainIconButton(
    { children, variant = "default", size = "lg", disabled = false, ...props },
    ref
) {
    return (
        <StyledButton
            {...props}
            ref={ref}
            type="button"
            $variant={variant}
            $size={size}
            disabled={disabled}
        >
            {children}
        </StyledButton>
    );
});

PlainIconButton.displayName = "PlainIconButton";

export default PlainIconButton;
